// @date_of_change: 2025-09-27 - GM role persistence on page refresh
// reason: Cache GM role in localStorage to survive page refresh

import { useState, useEffect } from 'react';

/**
 * Hook to manage and persist user role (GM/player) across page refreshes
 * Uses localStorage caching with server-side role verification
 */
export function useSessionRole(sessionId) {
  const [role, setRole] = useState('player');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!sessionId) {
      setLoading(false);
      return;
    }

    const cacheKey = `session_role_${sessionId}`;

    // Try to get cached role first for immediate UI
    const cachedRole = localStorage.getItem(cacheKey);
    if (cachedRole) {
      setRole(cachedRole);
      setLoading(false);
    } else {
      // @date_of_change: 2025-09-30 - Default to player for new sessions, WebSocket will update
      setRole('player');
      setLoading(false);
    }

    // @date_of_change: 2025-09-30 - Remove API verification since WebSocket provides authoritative role
    // The role will be set by the WebSocket joinCombat ACK response through updateRole()
    // This prevents conflicts between file-based session routes and live combat sessions
  }, [sessionId]);

  // A join acknowledgement can arrive before React commits the joined session
  // ID. Accept that authoritative target explicitly so the role cannot be
  // written into the previous/null room cache. date_of_change: 2026-08-18
  const updateRole = (newRole, targetSessionId = sessionId) => {
    const normalizedSessionId = String(targetSessionId || '').trim();
    const cacheKey = `session_role_${normalizedSessionId}`;
    setRole(newRole);
    if (normalizedSessionId) {
      localStorage.setItem(cacheKey, newRole);
    }
  };

  // Helper to clear cached role (e.g., when leaving session)
  const clearRole = () => {
    const cacheKey = `session_role_${sessionId}`;
    setRole('player');
    localStorage.removeItem(cacheKey);
  };

  return {
    role,
    isGM: role === 'gm',
    loading,
    updateRole,
    clearRole
  };
}
